"use client";

import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { useTranslations } from "next-intl";

import { isInAppPath } from "../utils/href";

/**
 * The design system's recessed circular ↗ chip — how a card offers "open this".
 *
 * The DS calls this the app's most-used control, and `--inner-button-shadow` is
 * already its exact inset shadow. White fill over the grey card, as the DS's own
 * dashboard reference overrides it: a `recessed` tone would otherwise sit on the
 * same grey as the chip fill and disappear.
 *
 * This is the ONLY way a widget should offer navigation. It replaced a
 * whole-card overlay link on the count tiles, which was wrong on two counts: the
 * DS gives every card an explicit ↗ affordance rather than making the surface
 * itself clickable, and an invisible full-bleed hit area meant selecting a number
 * or clicking anywhere in the tile navigated away unintentionally.
 *
 * Renders nothing at all unless the href is a validated in-app path, so a card
 * with nowhere to go simply has no chip — `isInAppPath` is what keeps a
 * backend-supplied `javascript:` or `//evil.com` from ever becoming a link.
 */
/**
 * The chip's own look, shared by the link and the button below.
 *
 * One constant because they are the same control: a card offers exactly one thing
 * in its corner, and whether that thing navigates or opens a panel is a detail of
 * the destination, not a reason to look different.
 */
export const WIDGET_CHIP_CLASS =
  "flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-input text-fg-muted shadow-[var(--inner-button-shadow)] transition-transform hover:text-foreground active:scale-95";

export function WidgetOpenLink({
  href,
  /** Accessible name; defaults to "view all". Pass the figure's own label on a
   *  count tile, where several chips can share one card. */
  label,
}: {
  href: string | null | undefined;
  label?: string;
}) {
  const t = useTranslations("Dashboard");

  if (!isInAppPath(href)) return null;

  const name = label ?? t("viewAll");

  return (
    <Link
      href={href}
      aria-label={name}
      title={name}
      className={WIDGET_CHIP_CLASS}
    >
      <ArrowUpRight className="size-3.5" />
    </Link>
  );
}

/**
 * The same ↗ chip, as a button, for a card whose "open this" has no URL.
 *
 * The chat widget is the case: chat is a global slide-over with no route, so the
 * card cannot link to it and used to point at a `/chat` path that nothing serves.
 * A button that opens the panel is the honest version of the same affordance, and
 * it looks identical because to the user it means the same thing.
 */
export function WidgetOpenButton({
  onClick,
  label,
}: {
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={WIDGET_CHIP_CLASS}
    >
      <ArrowUpRight className="size-3.5" />
    </button>
  );
}
